import { deleteField } from 'firebase/firestore';
import { ArrowLeft, History } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Field, FormSection } from '@/components/Admin/AdminFormFields';
import { AutosaveTextInput } from '@/components/Admin/Autosave/AutosaveTextInput';
import { ClosurePeriodsField } from '@/components/Admin/ClosurePeriodsField';
import { SlugField } from '@/components/Admin/SlugField';
import { Button } from '@/components/ui/button';
import { createSupplier, getSupplier, isSupplierIdTaken } from '@/data/suppliersRepo';
import { DocumentIdTakenError } from '@/data/util_documentIds';
import { HistoryPanel } from '@/hooks/autosave/HistoryPanel';
import { TopWhiteBarEditControls } from '@/hooks/autosave/TopWhiteBarEditControls';
import { UndoRedoButtons } from '@/hooks/autosave/UndoRedoButtons';
import { useAutosaveSupplier } from '@/hooks/autosave/useAutosaveSupplier';
import { undoRedoStorageKey, useUndoRedoStack } from '@/hooks/autosave/useUndoRedoStack';
import { countFormatter, describeChange, type ValueFormatterType } from '@/hooks/autosave/util_historyMessage';
import { useSaveStack } from '@/hooks/autosave/util_saveStack';
import { useExperiences } from '@/hooks/useExperiences';
import { closurePeriodDatesLabel, type ClosurePeriodType } from '@/types/types_closure';
import {
  coll_suppliersHistory,
  makeEmptySupplier,
  makeSupplierClosureId,
  supplierClosureEntries,
  type SupplierType,
} from '@/types/types_supplier';
import { todayCalendarDate } from '@/util/util_format';
import { slugify, validateSlugForCreate } from '@/util/util_slug';

interface PageSupplierEditProps {
  mode: 'new' | 'edit';
}

/**
 * Create / edit form for a single supplier.
 *
 * Same two-mode shape as the tour leader form: in `edit` mode every field
 * autosaves on commit (blur), with a sticky top bar, local undo/redo and
 * persisted history; in `new` mode there is a Create button, since there is
 * nothing to autosave until the document exists.
 *
 * The absences are the reason this record exists, so the screen says what they
 * reach: an admin closing a fortnight here is closing it on every experience
 * listed under "Runs", and ought to see that list while they do it.
 */
export function PageSupplierEdit({ mode }: PageSupplierEditProps) {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<SupplierType>(() => makeEmptySupplier());
  const formRef = useRef(form);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);

  const saveStack = useSaveStack();
  const autosave = useAutosaveSupplier(mode === 'edit' ? supplierId : undefined, saveStack);
  const undoRedo = useUndoRedoStack();
  const { loadFor: loadUndoRedoFor } = undoRedo;
  const { experiences } = useExperiences();
  const linked = experiences.filter((e) => e.supplierId === supplierId);

  useEffect(() => {
    if (mode !== 'edit' || !supplierId) return;
    let cancelled = false;
    getSupplier(supplierId)
      .then((supplier) => {
        if (cancelled) return;
        if (supplier) {
          formRef.current = supplier;
          setForm(supplier);
          loadUndoRedoFor(
            undoRedoStorageKey('supplier', supplierId),
            (field) => formRef.current[field as keyof SupplierType],
          );
        } else {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, supplierId, loadUndoRedoFor]);

  // Write a field value: reflect locally and, in edit mode, autosave it along
  // with a persisted history step. Shared by direct edits and by undo/redo.
  function applyChange(field: string, value: unknown, userAction: string, isUndoStep = false) {
    if (mode === 'edit') {
      const { newHistory } = autosave(userAction, { [field]: value }, formRef.current.history, isUndoStep);
      formRef.current = { ...formRef.current, [field]: value, history: newHistory };
      setForm(formRef.current);
    } else {
      formRef.current = { ...formRef.current, [field]: value };
      setForm(formRef.current);
    }
  }

  // A user edit: apply it and (edit mode) record it on the undo stack.
  function commit<K extends keyof SupplierType>(
    key: K,
    value: SupplierType[K],
    label: string,
    format?: ValueFormatterType,
  ) {
    const prevValue = formRef.current[key];
    if (Object.is(prevValue, value)) return;
    const describe = (v: unknown) => describeChange(label, v, format);
    applyChange(key, value, describe(value));
    if (mode === 'edit') {
      undoRedo.record({
        field: key,
        prevValue,
        nextValue: value,
        undoMessage: describe(prevValue),
        redoMessage: describe(value),
      });
    }
  }

  // ----- Absences -----
  //
  // Stored as a map, so an edit names the absence it touched —
  // `closurePeriods.{id}.reason` rather than a rewritten list. Two admins on the
  // same supplier keep each other's rows, and a poor connection sends one field
  // instead of the whole diary.
  //
  // Undo still moves the list as a whole, as the optional services on an
  // experience do: "put it back how it was" is about the list rather than one
  // row, and the undo stack stores whole field values anyway.
  const describeClosures = (value: unknown) =>
    describeChange('days away', value, countFormatter('closed period'));

  function saveClosures(
    next: Record<string, ClosurePeriodType>,
    addressed: Record<string, unknown>,
    userAction: string,
  ) {
    const prevValue = formRef.current.closurePeriods;

    if (mode !== 'edit') {
      formRef.current = { ...formRef.current, closurePeriods: next };
      setForm(formRef.current);
      return;
    }

    const { newHistory } = autosave(userAction, addressed, formRef.current.history);
    formRef.current = { ...formRef.current, closurePeriods: next, history: newHistory };
    setForm(formRef.current);

    undoRedo.record({
      field: 'closurePeriods',
      prevValue,
      nextValue: next,
      undoMessage: describeClosures(prevValue),
      redoMessage: describeClosures(next),
    });
  }

  function addClosure(period: ClosurePeriodType) {
    const id = makeSupplierClosureId();
    saveClosures(
      { ...formRef.current.closurePeriods, [id]: period },
      { [`closurePeriods.${id}`]: period },
      `Closed ${closurePeriodDatesLabel(period)}`,
    );
  }

  function removeClosure(id: string) {
    const live = formRef.current.closurePeriods[id];
    if (!live) return;
    const next = { ...formRef.current.closurePeriods };
    delete next[id];
    saveClosures(
      next,
      { [`closurePeriods.${id}`]: deleteField() },
      `Removed the closure on ${closurePeriodDatesLabel(live)}`,
    );
  }

  function changeClosureReason(id: string, reason: string) {
    const live = formRef.current.closurePeriods[id];
    if (!live || live.reason === reason) return;
    saveClosures(
      { ...formRef.current.closurePeriods, [id]: { ...live, reason } },
      { [`closurePeriods.${id}.reason`]: reason },
      reason
        ? `Gave the closure on ${closurePeriodDatesLabel(live)} the reason '${reason}'`
        : `Cleared the reason for the closure on ${closurePeriodDatesLabel(live)}`,
    );
  }

  // The id, written straight onto the form. Stable so that SlugField's effect —
  // which fills the field in from the name — does not re-run per render.
  const setSlug = useCallback((slug: string) => {
    formRef.current = { ...formRef.current, id: slug };
    setForm(formRef.current);
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setCreateError(null);
    // Checked again here, not only on blur: the field validates what was in it
    // when the user left it, and the list may have moved on since.
    const id = slugify(formRef.current.id || formRef.current.name);
    const idError = await validateSlugForCreate(id, isSupplierIdTaken, 'supplier');
    if (idError) {
      setSlugError(idError);
      setCreateError('Check the highlighted field above.');
      setSaving(false);
      return;
    }
    const toSave: SupplierType = { ...formRef.current, id };
    try {
      await createSupplier(toSave);
    } catch (e: unknown) {
      // The id was free a moment ago and is not any more — somebody else created
      // it in between, and the transaction refused rather than writing over them.
      if (e instanceof DocumentIdTakenError) {
        setSlugError('That id was taken while this form was open. Choose a different one.');
        setCreateError('Check the highlighted field above.');
      } else {
        console.error('Create supplier failed', e);
        setCreateError('The supplier could not be created. Try again.');
      }
      setSaving(false);
      return;
    }
    setSaving(false);
    navigate('/admin/suppliers');
  }

  if (loading) {
    return <div className='tw:py-16 tw:text-center tw:text-muted-foreground'>Loading…</div>;
  }

  if (notFound) {
    return (
      <div className='tw:py-16 tw:text-center'>
        <p className='tw:text-lg'>Supplier not found.</p>
        <Link to='/admin/suppliers' className='tw:mt-4 tw:inline-block tw:text-primary tw:underline'>
          Back to suppliers
        </Link>
      </div>
    );
  }

  const isEdit = mode === 'edit';

  const fields = (
    <div className='tw:space-y-8'>
      <FormSection title='Supplier'>
        <Field label='Name' required>
          <AutosaveTextInput currentValue={form.name} onCommitChange={(v) => commit('name', v, 'name')} />
        </Field>
        {!isEdit && (
          <SlugField
            label='Record ID'
            name={form.name}
            value={form.id}
            onChange={setSlug}
            isTaken={isSupplierIdTaken}
            entityLabel='supplier'
            error={slugError}
            onErrorChange={setSlugError}
          />
        )}
        <Field label='Notes' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            multiline
            rows={4}
            currentValue={form.notes}
            placeholder='How Eighty Days works with them — who to ring, how much notice they want.'
            onCommitChange={(v) => commit('notes', v, 'notes')}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            Internal. Partners never see this, and it is not on any public page.
          </p>
        </Field>
      </FormSection>

      <FormSection title='Days Away'>
        <div className='tw:sm:col-span-2'>
          <ClosurePeriodsField
            rows={supplierClosureEntries(form).map(({ id, period }) => ({ key: id, period }))}
            today={todayCalendarDate()}
            hint='Dates this supplier cannot work. Both days are included, and every experience linked to
              them is closed for these days as well.'
            emptyMessage='No absences recorded. Nothing is closed on the experiences this supplier runs.'
            onAdd={addClosure}
            onChangeReason={changeClosureReason}
            onRemove={removeClosure}
          />
        </div>
      </FormSection>

      {isEdit && (
        <FormSection title='Runs'>
          <div className='tw:sm:col-span-2'>
            {linked.length === 0 ? (
              <p className='tw:text-sm tw:text-muted-foreground'>
                No experiences are linked to this supplier yet. Link one from its own page, under
                Availability and Closures.
              </p>
            ) : (
              <>
                <p className='tw:mb-2 tw:text-sm tw:text-muted-foreground'>
                  These are closed on the days above, on top of their own closures.
                </p>
                <ul className='tw:divide-y tw:rounded-md tw:border'>
                  {linked.map((experience) => (
                    <li key={experience.id} className='tw:px-3 tw:py-2 tw:text-sm'>
                      <Link
                        to={`/admin/experiences/${experience.id}/edit`}
                        className='tw:underline tw:hover:text-ed-gold'
                      >
                        {experience.name || experience.id}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </FormSection>
      )}
    </div>
  );

  // ----- Edit mode: sticky autosave bar, no submit button -----
  if (isEdit) {
    return (
      <div className='tw:mx-auto tw:max-w-6xl'>
        <TopWhiteBarEditControls
          saveStack={saveStack}
          leftSlot={
            <Link
              to='/admin/suppliers'
              className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
            >
              <ArrowLeft className='tw:size-4' /> Suppliers
            </Link>
          }
          middleSlot={
            <UndoRedoButtons
              canUndo={undoRedo.canUndo}
              canRedo={undoRedo.canRedo}
              undoLabel={undoRedo.undoLabel}
              redoLabel={undoRedo.redoLabel}
              onUndo={() => undoRedo.undo((f, v, a) => applyChange(f, v, a, true))}
              onRedo={() => undoRedo.redo((f, v, a) => applyChange(f, v, a, true))}
            />
          }
        >
          <button
            type='button'
            onClick={() => setShowHistory(true)}
            className='tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-ed-stone tw:transition-colors tw:hover:text-ed-gold'
          >
            <History className='tw:size-4' /> History
            {!!form.history?.lastStep && <span className='tw:text-ed-stone/70'>({form.history.lastStep})</span>}
          </button>
        </TopWhiteBarEditControls>
        <header className='tw:mb-8'>
          <h1 className='tw:font-serif tw:text-3xl tw:font-light'>Edit — {form.name}</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>Changes save automatically.</p>
        </header>
        {fields}
        <HistoryPanel
          key={supplierId}
          show={showHistory}
          onClose={() => setShowHistory(false)}
          history={form.history}
          historyCollectionName={coll_suppliersHistory}
        />
      </div>
    );
  }

  // ----- New mode: create-then-navigate, no autosave -----
  return (
    <form onSubmit={handleCreate} className='tw:mx-auto tw:max-w-6xl'>
      <header className='tw:mb-8'>
        <Link
          to='/admin/suppliers'
          className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
        >
          <ArrowLeft className='tw:size-4' /> Suppliers
        </Link>
        <h1 className='tw:mt-2 tw:font-serif tw:text-3xl tw:font-light'>Add Supplier</h1>
      </header>

      {fields}

      {createError && <p className='tw:mt-6 tw:text-right tw:text-sm tw:text-destructive'>{createError}</p>}

      <div className='tw:mt-8 tw:flex tw:justify-end tw:gap-3'>
        <Button asChild variant='outline' type='button'>
          <Link to='/admin/suppliers'>Cancel</Link>
        </Button>
        <Button type='submit' disabled={!form.name.trim()} loading={saving}>
          {saving ? 'Saving…' : 'Add Supplier'}
        </Button>
      </div>
    </form>
  );
}
